// src/components/MapaPinDraggable.js
//
// Mini-mapa Google Maps con un pin arrastrable.
// Análogo al widget MapaPinDraggable del mobile (Flutter). Se usa para
// que el usuario ajuste el punto exacto de una ubicación (dirección
// base de un Guía, ubicación de un negocio o vecino, etc.).
//
// Requiere que REACT_APP_GOOGLE_MAPS_API_KEY esté definida. Si no,
// el componente se degrada a un mensaje informativo y las coordenadas
// se pueden editar por input numérico en el padre.
//
// Props:
//   lat, lng       : coordenadas actuales del pin. Si son null, se
//                    muestra centrado en la ubicación de fallback
//                    (default Bucaramanga) sin pin visible.
//   onChange({lat, lng}): callback cuando el usuario suelta el pin
//                    (onDragEnd) o hace click en el mapa.
//   height         : alto del mapa. Default 260.
//   zoom           : zoom inicial. Default 15.
//   direccionParaGeocodificar : texto opcional. Si viene y no hay
//                    lat/lng, intenta geocodificar la dirección.
//   sesgoUbicacion : {lat,lng} opcional. Centro real conocido de la
//                    zona (ej: coordenadas del municipio elegido) para
//                    sesgar el geocoder — más preciso que el fallback
//                    genérico de Bucaramanga cuando está disponible.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Alert, Typography, Button } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];   // para geocoding

// Fallback: centro de Bucaramanga
const CENTRO_DEFAULT = { lat: 7.11935, lng: -73.1227 };

export default function MapaPinDraggable({
  lat, lng, onChange, height = 260, zoom = 15,
  direccionParaGeocodificar, sesgoUbicacion,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: API_KEY || '',
    libraries: LIBRARIES,
  });
  const [mapa, setMapa] = useState(null);
  const [errorMapa, setErrorMapa] = useState('');

  const posicion = useMemo(() => {
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
      return { lat: Number(lat), lng: Number(lng) };
    }
    return null;
  }, [lat, lng]);

  // Centro para encuadrar el mapa Y para sesgar el geocoder: pin actual
  // > centro real de la zona conocida (sesgoUbicacion) > Bucaramanga.
  const centro = posicion || sesgoUbicacion || CENTRO_DEFAULT;

  // Recentrar cuando cambia la posición externa
  useEffect(() => {
    if (mapa && posicion) mapa.panTo(posicion);
  }, [mapa, posicion]);

  const capturarGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setErrorMapa('Este navegador no soporta geolocalización.');
      return;
    }
    setErrorMapa('');
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        // Antes esto fallaba en silencio (callback vacío). Códigos reales
        // de GeolocationPositionError: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE,
        // 3=TIMEOUT.
        const mensajes = {
          1: 'Bloqueaste el permiso de ubicación para este sitio. Habilítalo desde el candado de la barra de direcciones del navegador.',
          2: 'El navegador no pudo determinar tu ubicación actual.',
          3: 'Se agotó el tiempo esperando tu ubicación. Intenta de nuevo.',
        };
        setErrorMapa(mensajes[err.code] || 'No se pudo obtener tu ubicación.');
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [onChange]);

  const geocodificar = useCallback(() => {
    if (!direccionParaGeocodificar || !window.google) return;
    setErrorMapa('');
    const geocoder = new window.google.maps.Geocoder();

    // "bounds" sesga el resultado hacia la zona visible del mapa (no la
    // restringe de forma dura, como sí hace componentRestrictions). Sin
    // esto, "country: CO" deja a Google libre de resolver la dirección
    // en cualquier ciudad del país — así fue como una búsqueda hecha
    // desde Bucaramanga terminó devolviendo un resultado en Bogotá.
    // El sesgo usa el centro actual del mapa (el pin si ya hay uno, o
    // el fallback de Bucaramanga si el Guía todavía no tiene ubicación).
    const bounds = new window.google.maps.LatLngBounds(
      { lat: centro.lat - 0.6, lng: centro.lng - 0.6 },
      { lat: centro.lat + 0.6, lng: centro.lng + 0.6 },
    );

    geocoder.geocode({
      address: direccionParaGeocodificar,
      componentRestrictions: { country: 'CO' },
      region: 'CO',
      bounds,
    }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const p = results[0].geometry.location;
        onChange({ lat: p.lat(), lng: p.lng() });
      } else if (status === 'REQUEST_DENIED') {
        // Causa típica: la API key del navegador no tiene habilitada
        // "Geocoding API" (o no está en el proyecto GCP correcto).
        // Ver notas de sesión: la key web actual da PERMISSION_DENIED
        // en holaveci-502514 — pendiente crear una key nueva con
        // Maps JS + Geocoding + Places habilitadas para ese proyecto.
        setErrorMapa('La búsqueda de direcciones está bloqueada: la API key de Google Maps no tiene permiso de Geocoding. Ajusta las coordenadas arrastrando el pin mientras tanto.');
      } else if (status === 'ZERO_RESULTS') {
        setErrorMapa('No se encontró esa dirección en el mapa. Prueba agregando el barrio o simplifica el texto (ej. "Calle 45 #23-15"), o ajusta el pin arrastrándolo.');
      } else {
        setErrorMapa(`No se pudo buscar la dirección (${status}).`);
      }
    });
  }, [direccionParaGeocodificar, onChange, centro]);

  if (!API_KEY) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Para ver el mapa configura <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>{' '}
        en el archivo <code>.env</code>. Puedes seguir editando las coordenadas manualmente.
      </Alert>
    );
  }
  if (loadError) {
    return <Alert severity="error">No se pudo cargar Google Maps. Verifica la API key.</Alert>;
  }
  if (!isLoaded) {
    return <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
                       border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary">Cargando mapa…</Typography>
    </Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={capturarGPS}>
          Usar mi ubicación
        </Button>
        {direccionParaGeocodificar && (
          <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={geocodificar}>
            Buscar la dirección en el mapa
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
          {posicion
            ? `Pin: ${posicion.lat.toFixed(5)}, ${posicion.lng.toFixed(5)} — arrastra para ajustar`
            : 'Haz click en el mapa o usa tu ubicación'}
        </Typography>
      </Box>
      {errorMapa && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setErrorMapa('')}>
          {errorMapa}
        </Alert>
      )}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height, borderRadius: 8 }}
        center={centro}
        zoom={posicion ? zoom : 12}
        onLoad={setMapa}
        onClick={(e) => onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
        options={{
          streetViewControl: false, mapTypeControl: false, fullscreenControl: false,
        }}
      >
        {posicion && (
          <Marker
            position={posicion}
            draggable
            onDragEnd={(e) => onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
          />
        )}
      </GoogleMap>
    </Box>
  );
}
